import { Button, Tag as AntdTag } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import Icon from '../../components/Icon/Icon';
import Spinner from '../../components/Spinner/Spinner';
import Tag from '../../components/Tag/Tag';

import { Role } from '../../Helpers/AuthorizationDataProvider';
import useUpgradeRequest from './useUpgradeRequest';

import {
	Field,
	Status,
	UpgradeRequestModel,
	UserProfileModel,
} from './BackofficeUpgradeRequest.model';

import styles from './BackofficeUpgradeRequest.module.scss';
import moment from "moment";

const BackofficeUpgradeRequest = () => {
	const intl = useIntl();
	const { id } = useParams<{ id: string }>();
	const { onApproveRequest, onRejectRequest, state } = useUpgradeRequest({ requestId: id });
	const { loading, upgradeRequest, userProfile } = state;

	const profileFields: {[key:string]:any} = {
		personalInfo: {
			fields: [{
				id: 'name'
			}, {
				id: 'email'
			}, {
				id: 'location'
			}, {
				id: 'currentPosition'
			}],
			icon: {
				id: 'user-tie',
				size: '18'
			}
		},
		research: {
			fields: [{
				id: 'researchAreas'
			}, {
				id: 'institution'
			}],
			icon: {
				id: 'graduation-cap',
				size: '22'
			}
		},
	};
	
	const requestFields: {[key:string]:any} = {
		intent: {
			fields: [{
				extraClass: 'introduction',
				id: 'introduction'
			}, {
				extraClass: 'w-100',
				id: 'mentoringScopes'
			}],
			icon: {
				id: 'envelope',
				size: '18'
			}
		},
	};

	const getTagColorByRole = (role: Role) => {
		switch (role) {
			case 'Mentee': return 'grey';
			case 'Mentor': return 'blue';
		}
	};

	const renderChips = (chips: string[], category?: string) => (chips.map((field: string, index: number) => (
		<AntdTag
			key={`${field}${index}`}
			style={{
				border: 'none',
				textTransform: 'uppercase'
			}}>{category ? <FormattedMessage id={`${category}.${field}`} /> : field}
		</AntdTag>)
	));
	
	const renderField = <T extends {[key: string]: any}> (field: Field, application: T) => (
		<div className={`${styles.field} ${field.extraClass ? styles[field.extraClass] : ''}`} key={field.id}>
			<span className={styles.label}>
				{intl.formatMessage({id: `userLabels.${field.id}`})}
			</span>
			<div>{Array.isArray(application[field.id]) ? renderChips(application[field.id], field.id === 'researchAreas' ? 'researchAreas' : '') : application[field.id]}</div>
		</div>
	);

	const renderCategory = <T extends {[key: string]: any}>(category: string, data: T, fields: {[key:string]:any}) => (
		<div className={styles.category} key={category}>
			<div className={styles.categoryTitle}>
				<Icon
					name={fields[category].icon.id}
					color="light"
					width={fields[category].icon.size}
					height={fields[category].icon.size}
					className={styles.icon}
				/>
				<h2>{intl.formatMessage({ id: `userLabels.${category}` })}</h2>
			</div>
			<div className={styles.fields}>
				{fields[category].fields.map((field: Field) => renderField<T>(field, data))}
			</div>
		</div>
	);

	const renderApplicationIntent = (request: UpgradeRequestModel) => {
		return Object.keys(requestFields).map(key => renderCategory<UpgradeRequestModel>(key, request, requestFields));
	};
	
	const renderUserInformation = (profile: UserProfileModel) => {
		return Object.keys(profileFields).map(key => {
			return renderCategory<UserProfileModel>(key, profile, profileFields);
		})
	};

	return (<div className={styles.pageWrapper}>
		<div className={styles.profileImg}>
			<Icon name="user" width="60" height="60" color="light"/>
		</div>

		<div className={`${styles.card} ${styles.topBorder}`}>
			<div className={styles.actions}>
				<div className={styles.breadcrumbs}>
					<Link to={{ pathname: '/upgrade-request', state: { activeTab: 1 }}}><FormattedMessage id='navigation.upgradeRequests'/> /</Link>
					<span>{userProfile?.name}</span>
				</div>
				{upgradeRequest?.status === Status.Pending && <div>
					<Button size="large" className={styles.reject} onClick={onRejectRequest}>{intl.formatMessage({ id: 'buttons.reject' })}</Button>
					<Button size="large" className={styles.approve} onClick={onApproveRequest}><FormattedMessage id="buttons.approve" />
					</Button>
				</div>}
			</div>
			{upgradeRequest && userProfile && <div className={styles.cardContent}>
				<Spinner spinning={loading} />
				<div className={styles.applicationDateWrapper}>
					<FormattedMessage id="userLabels.appliedOn" />
					<span className={styles.applicationDate}>
						{moment(upgradeRequest.createdAt).format("DD MMM YYYY")}
					</span>
					<FormattedMessage id="basics.as" />
					<span className={styles.tag}>
						<Tag color={getTagColorByRole(userProfile.role)}>
							{userProfile.role}
						</Tag>
					</span>
			</div>
			<div>
				{renderUserInformation(userProfile)}
				{renderApplicationIntent(upgradeRequest)}
			</div>
			</div>}

		</div>
	</div>)
}

export default BackofficeUpgradeRequest;